import { useState } from 'react';
import { T } from '../../components/text/TextEdit';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { activeUiLanguage } from '../../lib/i18n';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

// Restyled to the mock; the behaviour below is unchanged from before, deliberately. A redesign of
// the sign-in screen is not the place to touch how signing in works.
export default function LoginForm() {
  const { t } = useTranslation();
  const { signInWithEmail, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const { error: err } = await signInWithEmail(email, password);
    setSubmitting(false);
    if (err) {
      setError(err.message);
      return;
    }
    // N-98: the account's resolved language, not the raw UI value - at this point
    // applyAccountLanguage has not necessarily seen the email yet.
    api.setLanguage(activeUiLanguage()).catch(() => {});
    navigate('/');
  };

  const onGoogle = async () => {
    setError('');
    const { error: err } = await signInWithGoogle();
    if (err) setError(err.message);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3.5" data-testid="login-form">
      <div>
        <label htmlFor="login-email" className="mb-1 block text-xs font-medium text-muted-foreground">
          {t('login.email')}
        </label>
        <Input
          id="login-email"
          type="email"
          required
          placeholder={t('login.emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="login-email-input"
        />
      </div>
      <div>
        <label htmlFor="login-password" className="mb-1 block text-xs font-medium text-muted-foreground">
          {t('login.password')}
        </label>
        <Input
          id="login-password"
          type="password"
          required
          minLength={6}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="login-password-input"
        />
      </div>

      {/* Dark --destructive in this install is hsl(0 62.8% 30.6%) - a dark red that is unreadable
          as text on a near-black background. An error nobody can read is worse than no styling. */}
      {error && (
        <p className="text-sm font-medium text-destructive dark:text-destructive" data-testid="login-error">
          {error}
        </p>
      )}

      <Button type="submit" disabled={submitting} className="w-full" data-testid="login-submit-button">
        {submitting ? t('login.submitting') : t('login.submit')}
      </Button>

      <div className="flex items-center gap-3 py-1.5">
        <span className="h-px flex-1 bg-border" />
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground"><T k="common.or" /></span>
        <span className="h-px flex-1 bg-border" />
      </div>

      <Button type="button" variant="outline" onClick={onGoogle} className="w-full" data-testid="login-google-button">
        {t('login.google')}
      </Button>
    </form>
  );
}